/**
 * EcolesCard — renders the Écoles & éducation block of the Environnement tab.
 *
 * Inputs : the parent fiche's id + geocoded point. Data is loaded via the
 * `useEcoles` hook (Firestore-cached, 30-day TTL, keyed on rounded lat/lng).
 *
 * UX spec (cf. docs/fiche-v2-plan.md § Phase 2) :
 *   - Liste groupée : maternelle / élémentaire / collège / lycée.
 *   - Chaque établissement : nom + chip "public/privé" + distance en mètres.
 *   - Groupes sans résultat masqués (pas de "0 maternelle" bruyant).
 */

import type { FicheDoc } from "../../lib/fiches";
import { useEcoles, type EcoleItem, type EcoleKind } from "../../lib/env/ecoles";
import { TIER_LABEL } from "../../lib/env/localityTier";
import { EnvCardShell } from "./EnvCardShell";
import { Ic } from "../icons";

type Props = { fiche: FicheDoc };

// Human-readable labels + display order.
const KIND_ORDER: { kind: EcoleKind; label: string }[] = [
  { kind: "maternelle", label: "Maternelle" },
  { kind: "primaire", label: "Primaire" },
  { kind: "elementaire", label: "Élémentaire" },
  { kind: "college", label: "Collège" },
  { kind: "lycee", label: "Lycée" },
  { kind: "autre", label: "Autre" },
];

export function EcolesCard({ fiche }: Props) {
  const state = useEcoles(fiche.id, fiche.geocode);

  // Derived summary for the subtitle — computed before the early returns so
  // the shell sees it in every status.
  const summary =
    state.status === "ready"
      ? summarise(state.data.items, state.data.radius_m, state.data.tier)
      : undefined;

  return (
    <EnvCardShell
      icon={<Ic.Users size={18} />}
      title="Écoles & éducation"
      subtitle={summary}
      state={state}
      footer={<>Source : data.education.gouv.fr · annuaire MENJ</>}
    >
      {state.status === "ready" && <EcolesList items={state.data.items} />}
    </EnvCardShell>
  );
}

// ─── Subtitle summary ───────────────────────────────────────────────────

function summarise(
  items: EcoleItem[],
  radiusM: number,
  tier: import("../../lib/env/localityTier").LocalityTier,
): string {
  const radiusLabel = `rayon ${formatRadius(radiusM)} · ${TIER_LABEL[tier]}`;
  if (items.length === 0) {
    return `Aucun établissement · ${radiusLabel}`;
  }
  const publicN = items.filter((i) => i.status === "public").length;
  const priveN = items.filter((i) => i.status === "prive").length;
  const parts: string[] = [`${items.length} établissements`];
  if (publicN) parts.push(`${publicN} public·s`);
  if (priveN) parts.push(`${priveN} privé·s`);
  parts.push(radiusLabel);
  return parts.join(" · ");
}

// ─── List ───────────────────────────────────────────────────────────────

function EcolesList({ items }: { items: EcoleItem[] }) {
  if (items.length === 0) {
    return (
      <div className="text-[13px] text-ink-600 py-2">
        Pas d'établissement scolaire référencé à proximité immédiate. Élargissez
        la recherche en consultant directement l'annuaire MENJ.
      </div>
    );
  }

  // Group by kind, preserve the KIND_ORDER display order. `autre` is folded
  // at the bottom and only shown if non-empty so the card stays focused.
  const groups = new Map<EcoleKind, EcoleItem[]>();
  for (const it of items) {
    const bucket = groups.get(it.kind) ?? [];
    bucket.push(it);
    groups.set(it.kind, bucket);
  }

  return (
    <div className="space-y-4">
      {KIND_ORDER.map(({ kind, label }) => {
        const bucket = groups.get(kind);
        if (!bucket || bucket.length === 0) return null;
        return (
          <div key={kind}>
            <div className="text-[11px] uppercase tracking-[0.1em] text-ink-500 font-medium mb-1.5">
              {label}{" "}
              <span className="text-ink-400 normal-case tracking-normal font-normal">
                · {bucket.length}
              </span>
            </div>
            <ul className="space-y-1">
              {bucket.slice(0, 6).map((it) => (
                <EcoleRow key={it.id} item={it} />
              ))}
              {bucket.length > 6 && (
                <li className="text-[11.5px] text-ink-500 pl-0.5">
                  + {bucket.length - 6} autres
                </li>
              )}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

function EcoleRow({ item }: { item: EcoleItem }) {
  return (
    <li className="flex items-center justify-between gap-3 py-1">
      <div className="min-w-0 flex-1">
        <div className="text-[13px] text-ink-900 truncate">{item.name}</div>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        {item.status === "public" && <StatusDot tone="ink">Public</StatusDot>}
        {item.status === "prive" && <StatusDot tone="amber">Privé</StatusDot>}
        <span className="text-[11.5px] text-ink-500 tabular w-12 text-right">
          {formatDistance(item.distance_m)}
        </span>
      </div>
    </li>
  );
}

function StatusDot({
  tone,
  children,
}: {
  tone: "ink" | "amber";
  children: React.ReactNode;
}) {
  const cls =
    tone === "ink"
      ? "bg-ink-100 text-ink-700"
      : "bg-amber-100 text-amber-800";
  return (
    <span
      className={`text-[10.5px] font-medium px-1.5 py-0.5 rounded-full ${cls}`}
    >
      {children}
    </span>
  );
}

function formatDistance(m: number): string {
  if (m < 950) return `${m} m`;
  return `${(m / 1000).toFixed(1).replace(".", ",")} km`;
}

// Variant for round radii — "10 km" reads cleaner than "10,0 km" in the
// card subtitle. Shared with CommercesCard via duplication ; factoring
// would cross a card boundary for 5 lines of code, not worth it.
function formatRadius(m: number): string {
  if (m < 1000) return `${m} m`;
  if (m % 1000 === 0) return `${m / 1000} km`;
  return `${(m / 1000).toFixed(1).replace(".", ",")} km`;
}
